/**
 * "Twin Circuits" — original advanced-interactions level (difficulty 4/10):
 * slopes → one-way stairs → dash-orb straightaway → teleport lift →
 * mirror section → dual corridor → blink-orb tunnel → finale.
 */
import type { LevelData, LevelObject } from '../../format/types';
import { UNITS_PER_BLOCK as B } from '../../engine/physics/constants';

let nextId = 1;
const objects: LevelObject[] = [];

function cell(bx: number, by: number): { x: number; y: number } {
  return { x: bx * B + B / 2, y: by * B + B / 2 };
}

function push(o: Omit<LevelObject, 'id'>): void {
  objects.push({ id: nextId++, ...o });
}

const spike = (bx: number, by = 0, hanging = false): void =>
  push({ type: 'spike', ...cell(bx, by), ...(hanging ? { flipY: true } : {}) });
const block = (bx: number, by = 0): void => push({ type: 'block', ...cell(bx, by) });
const slope = (bx: number, by: number, dir: 'up' | 'down', grade: 1 | 0.5 = 1): void =>
  push({ type: 'slope', ...cell(bx, by), props: { dir, grade } });
const oneway = (bx: number, by: number): void => push({ type: 'oneway', ...cell(bx, by) });
const portal = (bx: number, kind: string, by = 1, extra: Record<string, number> = {}): void =>
  push({ type: 'portal', ...cell(bx, by), props: { kind, ...extra } });
const orb = (bx: number, by: number, kind: string): void =>
  push({ type: 'orb', ...cell(bx, by), props: { kind } });
const ceiling = (bx0: number, bx1: number, by: number): void => {
  for (let bx = bx0; bx <= bx1; bx++) block(bx, by);
};

// --- Slope run (0–60) --------------------------------------------------------
spike(22);
slope(30, 0, 'up'); // ramp onto a plateau
block(31, 0);
block(32, 0);
slope(33, 0, 'down'); // ramp back down
spike(42);
slope(50, 0, 'up', 0.5); // gentle half-grade rise, then a small drop
spike(56);

// --- One-way stairs (62–95) --------------------------------------------------
oneway(66, 1);
oneway(70, 2);
oneway(74, 3);
// Spikes beneath the stairs punish falling off.
spike(70);
spike(71);
spike(74);
spike(75);
// Ride the top platform, drop past the end.
oneway(78, 3);
spike(79);
spike(80);

// --- Dash straightaway (96–130) ----------------------------------------------
// Dash orb before a long spike lake; hold to dash clean across at fixed height.
orb(96, 2, 'dash');
for (let bx = 99; bx <= 118; bx++) spike(bx);
// Release after the lake and land.

// --- Teleport lift (132–160) ---------------------------------------------------
// A tall wall blocks the path; the teleporter drops you onto the walkway above.
portal(140, 'teleport', 1, { dx: 2, dy: 6 });
for (let by = 0; by <= 4; by++) block(144, by); // the wall itself
ceiling(142, 152, 5); // walkway (top at y=180) — teleport lands here
spike(148, 6); // hazard on the high path
// Walkway ends at 152; drop back to the ground.

// --- Mirror section (164–195) ---------------------------------------------------
portal(164, 'mirror:on');
spike(172);
spike(180);
spike(181);
spike(188);
portal(194, 'mirror:off');

// --- Dual corridor (200–245) -----------------------------------------------------
portal(200, 'dual:on');
ceiling(202, 242, 7);
// Matched obstacles: ground spikes + hanging spikes at the same x, so one
// synced jump clears both bodies.
spike(210);
spike(211, 6, true);
spike(220);
spike(221, 6, true);
spike(230);
spike(231, 6, true);
portal(244, 'dual:off');

// --- Blink tunnel (250–285) -------------------------------------------------------
ceiling(250, 283, 5);
orb(254, 1, 'blink'); // blink up to the ceiling…
spike(258);
spike(259);
spike(260);
orb(266, 4, 'blink'); // …and back down
spike(270, 4, true);
spike(271, 4, true);

// --- Finale (288–300) ---------------------------------------------------------------
spike(290);
spike(291);
// Secret shards across the circuit halves.
push({ type: 'collectible', ...cell(40, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(110, 2), props: { kind: 'shard' } });
push({ type: 'collectible', ...cell(270, 2), props: { kind: 'shard' } });

push({ type: 'endgate', ...cell(300, 2) });

export const TWIN_CIRCUITS: LevelData = {
  formatVersion: 1,
  meta: {
    name: 'Twin Circuits',
    author: 'Shape Shifters Team',
    description: 'Slopes, dashes, mirrors — and a second you.',
    revision: 1,
    createdAt: '2026-07-23T00:00:00.000Z',
    updatedAt: '2026-07-23T00:00:00.000Z',
    difficulty: 4,
    tags: ['showcase', 'official'],
  },
  song: { id: 'circuit-breaker', bpm: 140, offsetMs: 0 },
  settings: {
    mode: 'normal',
    speed: 'one',
    startForm: 'runner',
    accentColor: '#f472b6',
    hazardColor: '#fbbf24',
    backgroundColor: '#1a0b2e',
  },
  objects,
};
